/**
 * BOOTSTRAP PREFETCH — fire the independent first-paint API calls the moment
 * the bundle executes, instead of after React mounts and effects run.
 *
 * Why: the three bootstrap calls are cheap on the server (GetWeekNumbers
 * 135ms / GetMetricDefinitions 94ms / GetPermissions 81ms p50, measured
 * 2026-08-27) but sat at the END of a serial chain: download 2.6MB → compile →
 * mount → effects → fetch. Firing them at module scope overlaps the network
 * round trips with React's own startup, taking their cost off the critical
 * path almost entirely.
 *
 * Why this is safe:
 * - All three are INDEPENDENT of each other. GetPermissions takes only
 *   {actorId, actorType} (identity, resolved server-side from Midway anyway);
 *   it does NOT consume week numbers — verified against
 *   permissionsApi.types.ts before parallelizing.
 * - RTK Query dedupes by endpoint+arg: when components later call
 *   useGetWeekNumbersQuery() etc., they JOIN these in-flight requests rather
 *   than re-issuing them. No consumer changes needed.
 * - Failures here are identical to failures from the hooks (same queryFn, same
 *   error shape lands in the same cache entry) — components render the same
 *   error states they always did.
 *
 * Definitions need {dashboardId, reviewType}; the dashboard comes from the URL
 * slug, parsed with the router's own helper. An unknown slug prefetches the
 *_default dashboard — the same one the router will redirect to.
 *
 * KEEP-ALIVE: initiate() opens a subscription that would pin the cache entry
 * forever; we release it after the page has settled. By then the components'
 * own hook subscriptions hold the data, and RTK's keepUnusedDataFor covers any
 * gap. 60s is far beyond the measured worst-case bootstrap (~13s LCP).
 */
import { store } from 'src/app/store';
import { weekNumbersApi } from 'src/features/api/weekNumbersApi';
import { metricDefinitionsApi } from 'src/features/api/metricDefinitionsApi';
import { permissionsApi } from 'src/features/api/permissionsApi';
import { parseDashboardSlug, DEFAULT_DASHBOARD_SLUG } from 'src/config/dashboards';
import { WBR_REVIEW_TYPE, ROUTER_BASENAME } from 'src/constants';

const RELEASE_SUBSCRIPTIONS_AFTER_MS = 60_000;

/**
 * RETRY-ONCE: the module-scope prefetch fires before ANY auth exists on a
 * cold session, racing the very first MCS login. Queries that lose the race
 * reject and — because nothing in RTK re-runs a rejected entry until a new
 * subscriber arrives — the page sits on skeletons until a human reloads
 * (the "hard reload ritual", reported 2026-09-02). One delayed re-dispatch
 * after the login settles converts that into an invisible ~2.5s catch-up.
 *
 * Only transient, auth/network-shaped failures are retried. 403 may also be
 * a genuine authorization denial: retrying once is harmless (it fails
 * identically and surfaces through the same error state, one request later)
 * and distinguishing the two client-side is not possible at this layer.
 */
const RETRY_AFTER_MS = 2_500;
const TRANSIENT_STATUSES = new Set([0, 401, 403]);

function isTransientBootstrapError(error: unknown): boolean {
  const status = (error as { status?: unknown } | undefined)?.status;
  return typeof status === 'number' && TRANSIENT_STATUSES.has(status);
}

/** First path segment after the router basename = the dashboard slug. */
export function dashboardIdFromLocation(pathname: string): string {
  const path = pathname.startsWith(ROUTER_BASENAME)
    ? pathname.slice(ROUTER_BASENAME.length)
    : pathname;
  const slug = path.split('/').filter(Boolean)[0];
  return (
    parseDashboardSlug(slug) ?? parseDashboardSlug(DEFAULT_DASHBOARD_SLUG)!
  );
}

export function prefetchBootstrapQueries(pathname: string): void {
  const dashboardId = dashboardIdFromLocation(pathname);

  // One launcher per endpoint so the retry can re-fire EXACTLY the queries
  // that failed, leaving successful cache entries untouched.
  const launchers = [
    (forceRefetch: boolean) =>
      store.dispatch(
        weekNumbersApi.endpoints.getWeekNumbers.initiate(undefined, {
          forceRefetch,
        }),
      ),
    (forceRefetch: boolean) =>
      store.dispatch(
        permissionsApi.endpoints.getPermissions.initiate(undefined, {
          forceRefetch,
        }),
      ),
    (forceRefetch: boolean) =>
      store.dispatch(
        metricDefinitionsApi.endpoints.getMetricDefinitions.initiate(
          { dashboardId, reviewType: WBR_REVIEW_TYPE },
          { forceRefetch },
        ),
      ),
  ];

  const subscriptions = launchers.map((launch) => launch(false));

  // Retry-once (see block comment above). The initiate() promise RESOLVES
  // with the result even on failure, so Promise.all never rejects here.
  // Retry subscriptions get their OWN release schedule: sharing the initial
  // release array would leak them whenever the initial queries settle
  // slower than the release window — precisely the stall scenario this
  // retry exists for.
  void Promise.all(subscriptions).then((results) => {
    const failedLaunchers = launchers.filter(
      (_, i) =>
        results[i].isError && isTransientBootstrapError(results[i].error),
    );
    if (failedLaunchers.length === 0) return;
    setTimeout(() => {
      // forceRefetch: unambiguous "run again now" for an entry whose
      // previous attempt rejected.
      const retrySubscriptions = failedLaunchers.map((launch) => launch(true));
      setTimeout(() => {
        retrySubscriptions.forEach((subscription) =>
          subscription.unsubscribe(),
        );
      }, RELEASE_SUBSCRIPTIONS_AFTER_MS);
    }, RETRY_AFTER_MS);
  });

  setTimeout(() => {
    subscriptions.forEach((subscription) => subscription.unsubscribe());
  }, RELEASE_SUBSCRIPTIONS_AFTER_MS);
}
